import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import QueueSlide from "../components/QueueSlide";
import Loader from "../components/Loader";
import { useUserData } from "../lib/contexts/UserContext";
import { useHospDocData } from "../lib/contexts/HospitalDoctorContext";
import { getResourcesByHospitalId } from "../lib/apis/resources";
import { ResourcesType } from "../lib/utils/types";
import { useNavigate } from "react-router-dom";
import { useInterval } from "../lib/utils/useInterval";
import { Carousel } from "react-responsive-carousel";
import { HiArrowLeft, HiOutlineHome } from "react-icons/hi2";
import Marquee from "react-fast-marquee";

// ✅ Cookie helpers
const setCookie = (name: string, value: string, days = 365) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
};

const getCookie = (name: string) => {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? match[2] : null;
};

// `category` is a free-form string, so a strict === "video" check sent values
// like "Video" or "video/mp4" down the <img> branch, rendering a video URL as a
// broken image — and since the rotation timer only armed for === "image", the
// panel then sat on that broken frame forever.
const isVideoRes = (r?: ResourcesType) =>
  !!r?.category?.toLowerCase().includes("video");

interface CarouselScreenProps {
  isToggled: boolean;
}

const CarouselScreen: React.FC<CarouselScreenProps> = ({ isToggled }) => {
  const { userData } = useUserData();
  const { hospitalID, doctors } = useHospDocData();
  const navigate = useNavigate();

  const [resources, setResources] = useState<ResourcesType[]>([]);
  const [currentResourceIndex, setCurrentResourceIndex] = useState<number>(0);
  const [time, setTime] = useState(moment().format("hh:mm:ss A"));

  // ✅ pageSize state with cookie persistence
  const [pageSize, setPageSize] = useState<number>(() => {
    const stored = getCookie("pageSize");
    return stored ? Number(stored) : 5;
  });

  // ✅ State for controlled carousel to enable auto-jump on ongoing patient
  const [selectedSlide, setSelectedSlide] = useState<number>(0);
  // ✅ Disable auto-jump during initial load to prevent glitchy behavior
  const [enableAutoJump, setEnableAutoJump] = useState<boolean>(false);

  // Enable auto-jump after initial load period (10 seconds)
  useEffect(() => {
    const timer = setTimeout(() => {
      setEnableAutoJump(true);
    }, 10000);
    return () => clearTimeout(timer);
  }, []);

  // Fetch resources from API
  const fetchResources = async () => {
    try {
      const response = await getResourcesByHospitalId(String(hospitalID));
      if (response.data.status === "SUCCESS") {
        setResources(response.data.result);
      }
    } catch (error) {
      console.error("Error fetching resources:", error);
    }
  };

  // Fetch resources on component mount
  useEffect(() => {
    fetchResources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hospitalID]);

  // Update time every second
  useInterval(() => {
    setTime(moment().format("hh:mm:ss A"));
  }, 1000);

  // Navigate to admin if doctors data is missing
  useEffect(() => {
    if (!doctors) navigate("/" + hospitalID);
  }, [doctors, navigate, hospitalID]);

  // A resource that never plays used to latch this panel, because advancement
  // only happened on a video's onEnded or an image's timer. skip() steps past
  // the bad one; strikesRef stops us cycling forever when all are broken.
  const strikesRef = useRef(0);
  const [adsDead, setAdsDead] = useState(false);

  const skip = (reason: string) => {
    console.warn(
      "[tv-ads] skipping resource:",
      reason,
      resources[currentResourceIndex]?.resource_url
    );
    strikesRef.current += 1;
    if (strikesRef.current >= resources.length) {
      setAdsDead(true); // every resource failed; fall back to the placeholder
      return;
    }
    setCurrentResourceIndex((prev) => (prev + 1) % resources.length);
  };

  // Handle automatic switching of resources
  useEffect(() => {
    if (resources.length > 0) {
      // Anything that isn't a video rotates on a timer, independent of whether
      // it loaded. Previously gated on category === "image", so an unrecognised
      // category never armed the timer at all.
      if (!isVideoRes(resources[currentResourceIndex])) {
        const timer = setTimeout(() => {
          setCurrentResourceIndex(
            (prevIndex) => (prevIndex + 1) % resources.length
          );
        }, 5000);
        return () => clearTimeout(timer);
      }
    }
  }, [currentResourceIndex, resources]);

  // Last resort. onError covers a resource that fails, but a request that simply
  // hangs (weak clinic wifi, captive portal) fires NO event at all — not error,
  // not ended — which is what leaves this panel blank forever. If nothing has
  // started 30s after we switched to a resource, give up on it.
  const startedRef = useRef(false);
  useEffect(() => {
    startedRef.current = false;
  }, [currentResourceIndex]);

  useEffect(() => {
    if (resources.length === 0 || adsDead) return;
    const t = setTimeout(() => {
      if (!startedRef.current) skip("nothing played within 30s");
    }, 30000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentResourceIndex, resources.length, adsDead]);

  // Handle video end event
  const handleVideoEnd = () => {
    if (resources.length > 0) {
      setCurrentResourceIndex(
        (prevIndex) => (prevIndex + 1) % resources.length
      );
    }
  };

  // ✅ function to update pageSize + cookie
  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCookie("pageSize", String(newSize), 30);
  };

  // ✅ Build mapping of doctor mapping_id to slide index for auto-jump
  const mappingIdToSlide: Record<string, number> = {};
  doctors?.forEach((doc, idx) => {
    if (doc?.mapping_id) {
      mappingIdToSlide[String(doc.mapping_id)] = idx;
    }
  });

  // ✅ Callback handler for when a doctor's ongoing patient changes
  const handleOngoingFromChild = (mappingId: string) => {
    // Skip auto-jump during initial load period to prevent glitchy behavior
    if (!enableAutoJump) return;
    
    const idx = mappingIdToSlide[mappingId];
    if (typeof idx === "number" && idx !== selectedSlide) {
      setSelectedSlide(idx);
    }
  };

  // JSX Render
  return (
    <>
      <div className="fixed py-1 bg-blue-500 z-[999] w-[100vw]">
        <Marquee className="text-white " direction="right" speed={100}>
          Please check in upon entering the clinic
        </Marquee>
      </div>
      {userData ? (
        <div className="flex flex-col md:flex-row ">
          {doctors?.length === 1 && (
            <>
              <div
                className={
                  isToggled
                    ? "md:w-[50%] h-full p-4 "
                    : "md:w-[100%] px-10 h-full p-4"
                }
              >
                <div className={`flex justify-between items-center w-full bg-white ${isToggled ? 'sticky top-[40px] z-[998] mb-4' : 'sticky top-[40px] z-[998] mb-6'}`}>
                  <div className="flex gap-4 items-center">
                    <div
                      className="bg-white p-3 rounded-full m-4 shadow-md cursor-pointer 
                       hover:bg-blue-500 hover:text-white transition-all duration-200"
                      onClick={() => navigate("/" + hospitalID)}
                    >
                      <HiArrowLeft
                        className="w-6 h-6 text-blue-500" 
                        style={{ color: 'inherit' }} // This ensures the 'hover:text-white' from parent works
                      />
                    </div>
                    <p className="flex  text-2xl ">{time}</p>
                  </div>
                  <img
                    src={require("../assets/images/DTlogo.png")}
                    alt="Queue empty"
                    className="flex w-[8rem] h-[3rem] cursor-pointer z-40"
                    onClick={() => navigate("/admin")}
                  />
                </div>
                {doctors?.map((doc, index) => (
                  <QueueSlide
                    key={index}
                    mapping_id={doc.mapping_id}
                    carouselView={false}
                    isToggled={isToggled}
                    showSelect={true}
                    pageSize={pageSize}
                    setPageSize={handlePageSizeChange}
                  />
                ))}
              </div>

              {isToggled && (
                <div className="w-[50%] h-full md:h-[100vh] items-right fixed top-0 right-0">
                  <div className="h-full w-full flex justify-center items-center ">
                    {resources.length > 0 && !adsDead ? (
                      isVideoRes(resources[currentResourceIndex]) ? (
                        <video
                          src={resources[currentResourceIndex]?.resource_url}
                          autoPlay
                          muted
                          playsInline
                          loop={resources.length === 1}
                          className="w-full h-full object-cover"
                          onPlaying={() => {
                            strikesRef.current = 0;
                            startedRef.current = true;
                          }}
                          onEnded={
                            resources.length === 1 ? undefined : handleVideoEnd
                          }
                          onError={() => skip("video failed to load")}
                        />
                      ) : (
                        <img
                          src={resources[currentResourceIndex]?.resource_url}
                          alt={`Resource ${resources[currentResourceIndex]?.resource_id}`}
                          className="w-full h-full object-cover"
                          onLoad={() => {
                            strikesRef.current = 0;
                            startedRef.current = true;
                          }}
                          onError={() => skip("image failed to load")}
                        />
                      )
                    ) : (
                      <video
                        src="https://ik.imagekit.io/gn9by35ed/Button%20(Landscape)%20(1).webm/ik-video.mp4?updatedAt=1734682888467"
                        autoPlay
                        loop
                        muted
                      />
                    )}
                  </div>
                </div>
              )}
            </>
          )}
          {doctors && doctors?.length > 1 && (
            <>
              <div
                className={`${isToggled ? "md:w-1/2" : "md:w-full"} h-full p-4`}
              >
                <div className={`flex justify-between items-center m-auto w-[98%] bg-white py-2 ${isToggled ? 'sticky top-[40px] z-[998] mb-4 ' : 'sticky top-[40px] z-[998] !mb-6 '}`}>
                  <div className="flex gap-4 items-center">
                    <div
                      className="bg-white p-3 rounded-full shadow-md cursor-pointer 
                       hover:bg-blue-500 hover:text-white transition-all duration-200"
                      onClick={() => navigate("/" + hospitalID + "/choose-carousel-doctors")}
                    >
                      <HiArrowLeft
                        className="w-6 h-6 text-blue-500" 
                        style={{ color: 'inherit' }} // This ensures the 'hover:text-white' from parent works
                      />
                    </div>
                    <div
                      className="bg-white p-3 rounded-full shadow-md cursor-pointer 
                       hover:bg-blue-500 hover:text-white transition-all duration-200"
                      onClick={() => navigate("/" + hospitalID)}
                    >
                      <HiOutlineHome
                        className="w-6 h-6 text-blue-500" 
                          style={{ color: 'inherit' }} // This ensures the 'hover:text-white' from parent works
                        />
                    </div>
                    <p className="flex  text-2xl ">{time}</p>
                  </div>
                  <img
                    src={require("../assets/images/DTlogo.png")}
                    alt="Queue empty"
                    className="flex w-[8rem] h-[3rem] cursor-pointer z-40"
                    onClick={() => navigate("/admin")}
                  />
                </div>
                <Carousel
                  autoPlay
                  infiniteLoop
                  interval={pageSize * 1000 + 8000}
                  showThumbs={false}
                  showStatus={false}
                  showIndicators={false}
                  stopOnHover={false}
                  selectedItem={selectedSlide}
                  onChange={(i) => setSelectedSlide(i)}
                >
                  {doctors?.map((doc, index) => (
                    <QueueSlide
                      key={index}
                      mapping_id={doc.mapping_id}
                      carouselView={true}
                      isToggled={isToggled}
                      showSelect={true}
                      pageSize={pageSize}
                      setPageSize={handlePageSizeChange}
                      onOngoingChange={handleOngoingFromChild}
                    />
                  ))}
                </Carousel>
              </div>
              {isToggled && (
                <div className="w-[50%] h-full md:h-[100vh] items-right fixed top-0 right-0">
                  <div className="h-full w-full flex justify-center items-center ">
                    {resources.length > 0 && !adsDead ? (
                      isVideoRes(resources[currentResourceIndex]) ? (
                        <video
                          src={resources[currentResourceIndex]?.resource_url}
                          autoPlay
                          muted
                          playsInline
                          loop={resources.length === 1}
                          className="w-full h-full object-cover"
                          onPlaying={() => {
                            strikesRef.current = 0;
                            startedRef.current = true;
                          }}
                          onEnded={
                            resources.length === 1 ? undefined : handleVideoEnd
                          }
                          onError={() => skip("video failed to load")}
                        />
                      ) : (
                        <img
                          src={resources[currentResourceIndex]?.resource_url}
                          alt={`Resource ${resources[currentResourceIndex]?.resource_id}`}
                          className="w-full h-full object-cover"
                          onLoad={() => {
                            strikesRef.current = 0;
                            startedRef.current = true;
                          }}
                          onError={() => skip("image failed to load")}
                        />
                      )
                    ) : (
                      <video
                        src="https://ik.imagekit.io/gn9by35ed/Button%20(Landscape)%20(1).webm/ik-video.mp4?updatedAt=1734682888467"
                        autoPlay
                        loop
                        muted
                      />
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        <Loader />
      )}
    </>
  );
};

export default CarouselScreen;
